// components/EmailEditor/TemplatesDialog.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  Box,
  TablePagination,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Close, Search as SearchIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../services/api";

const TemplatesDialog = ({ open, onClose, onSelectTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [templateShow, setTemplateShow] = useState({ id: null, subject: "", html: "" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (open) {
      setPage(0);
      setSearchTerm("");
      fetchTemplates(1, rowsPerPage);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchTemplates(page + 1, rowsPerPage);
    }
  }, [page, rowsPerPage]);

  const fetchTemplates = async (pageNum = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await api.get("/schedule_email/get_email_templates", {
        params: { page: pageNum, page_size: pageSize },
      });
      if (response.data.success) {
        setTemplates(response.data.data);
        setTotalCount(response.data.total_count || 0);
      } else {
        toast.error("Failed to fetch templates");
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error(error.response?.data?.detail || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    onSelectTemplate(template);
    onClose();
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength = 50) => {
    if (!text) return "—";
    const strText = typeof text === "string" ? text : JSON.stringify(text);
    return strText.length > maxLength
      ? strText.substring(0, maxLength) + "..."
      : strText;
  };

  // Parse JSON safely
  const parseJSON = (data) => {
    try {
      return typeof data === "string" ? JSON.parse(data) : data;
    } catch {
      return data;
    }
  };

  // Filter templates by subjectFor search
  const filteredTemplates = templates.filter((template) => {
    if (!searchTerm.trim()) return true;
    const parsed = parseJSON(template.toCcBccSubject);
    const subject = typeof parsed === "object" ? (parsed.subjectFor || "") : String(parsed || "");
    return subject.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "#f8fafc",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 2,
        }}
      >
        <Typography variant="h5" fontWeight="600">
          Email Templates
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
          {/* Search Bar */}
          <Paper
            sx={{
              p: 2,
              mb: 2,
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <TextField
              placeholder="Search by subject..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9CA3AF", fontSize: "1.2rem" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 320,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "10px",
                  "&.Mui-focused fieldset": {
                    borderColor: "#6366f1",
                  },
                },
              }}
              size="small"
            />
            <Typography variant="body2" sx={{ color: "#64748b", ml: "auto" }}>
              Showing <strong>{filteredTemplates.length}</strong> of <strong>{templates.length}</strong> templates
            </Typography>
          </Paper>
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 800,
              boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
              borderRadius: 2,
            }}
          >
            <div className="overflow-y-auto max-h-[50vh]">
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "#6366f1",
                      color: "white",
                      fontWeight: 600,
                      width: "80px",
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#6366f1",
                      color: "white",
                      fontWeight: 600,
                    //   minWidth: "200px",
                    }}
                  >
                    To/Cc/Bcc/Subject
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#6366f1",
                      color: "white",
                      fontWeight: 600,
                    //   minWidth: "300px",
                    }}
                  >
                    HTML Template
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#6366f1",
                      color: "white",
                      fontWeight: 600,
                    //   minWidth: "200px",
                    }}
                  >
                    Attachments
                  </TableCell>
                  {/* <TableCell
                    sx={{
                      backgroundColor: "#6366f1",
                      color: "white",
                      fontWeight: 600,
                      minWidth: "200px",
                    }}
                  >
                    Metadata
                  </TableCell> */}
                  <TableCell
                    sx={{
                      backgroundColor: "#6366f1",
                      color: "white",
                      fontWeight: 600,
                      width: "120px",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTemplates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No templates found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTemplates.map((template) => {
                    const toCcBcc = parseJSON(template.toCcBccSubject);
                    const attachments = parseJSON(template.attachments);
                    // const metadata = parseJSON(template.metadata);

                    return (
                      <TableRow
                        key={template.id}
                        onClick={
                            () => {
                                const subject = typeof toCcBcc === "object" ? (toCcBcc.subjectFor || "N/A") : "N/A";
                                setTemplateShow({ id: template.id, subject, html: template.html_temp });
                                setShowTemplate(true);}
                        }
                        hover
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                          },
                        }}
                      >
                        <TableCell>{template.id}</TableCell>

                        <Tooltip
                          title={
                            <div style={{ whiteSpace: "pre-wrap" }}>
                              {typeof toCcBcc === "object"
                                ? JSON.stringify(toCcBcc, null, 2)
                                : toCcBcc || "N/A"}
                            </div>
                          }
                          placement="top"
                          arrow
                        >
                          <TableCell sx={{ cursor: "pointer" }}>
                            {truncateText(
                              typeof toCcBcc === "object"
                                ? `Subject: ${toCcBcc.subjectFor || "N/A"}`
                                : toCcBcc
                            )}
                          </TableCell>
                        </Tooltip>

                        {/* <Tooltip
                          title={
                            <div
                              style={{
                                maxHeight: "400px",
                                overflow: "auto",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {template.html_temp || "N/A"}
                            </div>
                          }
                          placement="top"
                          arrow
                        > */}
                          <TableCell sx={{ cursor: "pointer" }}>
                            {truncateText(template.html_temp, 80)}
                          </TableCell>
                        {/* </Tooltip> */}

                        <Tooltip
                          title={
                            <div style={{ whiteSpace: "pre-wrap" }}>
                              {Array.isArray(attachments)
                                ? attachments.map((a, i) => (
                                    <div key={i}>
                                      {a.name}: {a.link}
                                    </div>
                                  ))
                                : attachments || "No attachments"}
                            </div>
                          }
                          placement="top"
                          arrow
                        >
                          <TableCell sx={{ cursor: "pointer" }}>
                            {Array.isArray(attachments)
                              ? `${attachments.length} file(s)`
                              : truncateText(attachments)}
                          </TableCell>
                        </Tooltip>

                        {/* <Tooltip
                          title={
                            <div style={{ whiteSpace: "pre-wrap" }}>
                              {typeof metadata === "object"
                                ? JSON.stringify(metadata, null, 2)
                                : metadata || "N/A"}
                            </div>
                          }
                          placement="top"
                          arrow
                        >
                          <TableCell sx={{ cursor: "pointer" }}>
                            {truncateText(
                              typeof metadata === "object"
                                ? `${
                                    Array.isArray(metadata)
                                      ? metadata.length
                                      : Object.keys(metadata).length
                                  } items`
                                : metadata
                            )}
                          </TableCell>
                        </Tooltip> */}

                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleSelectTemplate(template)}
                            sx={{
                              textTransform: "none",
                              backgroundColor: "#6366f1",
                              "&:hover": {
                                backgroundColor: "#4f46e5",
                              },
                            }}
                          >
                            Use
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            </div>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: "1px solid #e2e8f0",
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  color: "#64748b",
                  fontSize: "0.875rem",
                },
              }}
            />
          </TableContainer>
          </>
        )}
        {showTemplate && (
            <Paper
              sx={{
                mt: 2,
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                border: "1px solid #e2e8f0",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.5,
                  backgroundColor: "#6366f1",
                  color: "white",
                }}
              >
                <Typography variant="body1" fontWeight={600}>
                  #{templateShow.id} — {templateShow.subject}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowTemplate(false)}
                  sx={{ color: "white" }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
              <div
                style={{ padding: "16px", backgroundColor: "#fff" }}
                dangerouslySetInnerHTML={{ __html: templateShow.html }}
              />
            </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 4, py: 2, backgroundColor: "#f8fafc" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderColor: "#6366f1",
            color: "#6366f1",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplatesDialog;
